import { motion } from "framer-motion";
import { Star, Quote, ExternalLink } from "lucide-react";
import { useLang } from "@/App";
import { copy, reviewsData, CONTACT } from "@/i18n";

const Stars = ({ n }) => (
    <span className="inline-flex gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
            <Star
                key={i}
                size={13}
                className={i <= n ? "fill-brass text-brass" : "text-neutral-600"}
            />
        ))}
    </span>
);

const Reviews = () => {
    const { lang } = useLang();
    const c = copy[lang].reviews;

    return (
        <section id="reviews" data-testid="reviews-section" className="relative py-20 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
                <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 28 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                    >
                        <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-crimson">
                            {c.label}
                        </span>
                        <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl">
                            {c.title}
                        </h2>

                        <div data-testid="google-rating-badge" className="mt-8 flex items-end gap-4">
                            <span className="font-display font-extrabold text-6xl sm:text-7xl leading-none">4.7</span>
                            <div className="pb-1.5">
                                <Stars n={5} />
                                <p className="mt-1 text-sm text-neutral-400">
                                    172 {c.google}
                                </p>
                            </div>
                        </div>

                        <a
                            data-testid="reviews-google-link"
                            href={CONTACT.google}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 inline-flex items-center gap-2 text-sm text-crimson-bright hover:text-white transition-colors font-semibold"
                        >
                            {c.seeAll}
                            <ExternalLink size={14} />
                        </a>
                    </motion.div>

                    <div className="grid gap-4">
                        {reviewsData.map((r, i) => (
                            <motion.blockquote
                                key={i}
                                data-testid={`review-card-${i}`}
                                initial={{ opacity: 0, x: 28 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6, delay: i * 0.12 }}
                                className="rounded-2xl border border-white/10 bg-coal/60 p-6 flex gap-4"
                            >
                                <Quote size={22} className="text-crimson shrink-0 mt-1" />
                                <div>
                                    <p className="text-neutral-200 font-serif italic text-base sm:text-lg leading-relaxed">
                                        “{r.text[lang]}”
                                    </p>
                                    <footer className="mt-3 flex items-center gap-3">
                                        <Stars n={r.stars} />
                                        <span className="text-xs font-semibold text-neutral-300">{r.author}</span>
                                        <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">
                                            · {r.source}
                                        </span>
                                    </footer>
                                </div>
                            </motion.blockquote>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Reviews;
